<template>
  <v-card v-if="widget" class="kpi-card" :color="color" :dark="isDark">
    <v-card-title class="d-flex align-center pb-0">
      <div class="subheading font-weight-bold">
        <span :class="textColor">{{ widget.title }}</span>
      </div>
    </v-card-title>
    <v-card-text class="d-flex align-center pt-0">
      <div class="loader-wrapper" v-if="!widget.loaded">
        <base-loader></base-loader>
      </div>
      <div v-else>
        <div v-if="currentMonth && previousMonth">
          <div class="d-inline-flex display-3" :class="countColorClass">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="pr-2">info</v-icon>
              </template>
              <span>{{ translation(`data.${widgetKey}.delta-tooltip`)}}</span>
            </v-tooltip>
            <span>{{ currentMonth.value }}</span>
            <v-icon v-if="computedPercentageDifference > 0" size="20" color="green">check</v-icon>
            <v-icon v-if="computedPercentageDifference < 0" size="20" color="red">priority_high</v-icon>
          </div>
          <div>
            <span>{{ translation(`data.chart.previous-month`)}}: {{ previousMonth.value }}</span>
            <span v-if="computedPercentageDifference"> ({{ computedPercentageDifference >= 0 ? '+' : ''}}{{ computedPercentageDifference | formatNumber }}%)</span>
          </div>
          <div class="font-weight-bold">{{ translation(`data.value.${currentMonth.month}`) }}</div>
        </div>
        <div v-else-if="currentMonth || previousMonth || unknownMonth">
          <div class="d-inline-flex display-3">
            <span v-if="currentMonth">{{ currentMonth.value }}</span>
            <span v-else-if="previousMonth">{{ previousMonth.value }}</span>
            <span v-else-if="unknownMonth">{{ unknownMonth.value }}</span>
          </div>
          <div v-if="currentMonth" class="font-weight-bold">{{ translation(`data.value.${currentMonth.month}`) }}</div>
          <div v-else-if="previousMonth" class="font-weight-bold">{{ translation(`data.value.${previousMonth.month}`) }}</div>
          <div v-else-if="unknownMonth" class="font-weight-bold">{{ unknownMonth.month }}</div>
        </div>
        <div v-else class="headline">{{ translation('widget.kpi.no-data') }}</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { eventBus } from '@/eventBus'
import BaseLoader from '@/components/loaders/BaseLoader'
import { mapGetters } from 'vuex'

export default {
  name: 'WidgetKPI',
  components: {
    BaseLoader
  },
  props: {
    widgetKey: { type: String, required: true },
    numberFormatting: { type: String, required: false, default: null },
    useDecimal: { type: Boolean, required: false, default: false },
    color: { type: String, required: false },
    textColor: { type: String, required: false },
    isDark: { type: Boolean, required: false, default: false }
  },
  computed: {
    ...mapGetters(['translation']),
    widget: {
      get () { return this.$store.getters.getChartByKey(this.widgetKey) }
    },
    computedPercentageDifference () {
      let computedPercentageDifference
      if (this.currentMonth && this.previousMonth && this.previousMonth.value !== 0) {
        computedPercentageDifference = (this.currentMonth.value - this.previousMonth.value) * 100 / this.previousMonth.value
      }
      return computedPercentageDifference
    },
    countColorClass () {
      if (this.currentMonth && this.previousMonth) {
        const result = this.currentMonth.value - this.previousMonth.value
        if (result > 0) {
          return 'green--text'
        } else if (result < 0) {
          return 'red--text'
        }
      }
      return ''
    },
    previousMonth () {
      let previousMonth
      if (this.widget && this.widget.data && this.widget.data.result && this.widget.data.result.length) {
        previousMonth = this.widget.data.result.find(item => item.sort_column === 0)
      }
      return previousMonth
    },
    currentMonth () {
      let currentMonth
      if (this.widget && this.widget.data && this.widget.data.result && this.widget.data.result.length) {
        currentMonth = this.widget.data.result.find(item => item.sort_column === 1)
      }
      return currentMonth
    },
    unknownMonth () {
      let unknownMonth
      if (this.widget && this.widget.data && this.widget.data.result && this.widget.data.result.length && !this.previousMonth && !this.currentMonth) {
        unknownMonth = this.widget.data.result[0]
      }
      return unknownMonth
    }
  },
  filters: {
    formatNumber: function (value) {
      if (value % 1 !== 0) {
        return parseFloat(value).toFixed(2)
      } else {
        return value
      }
    }
  },
  created () {
    eventBus.$on('update-dashboard', () => {
      this.$log.info('Fetching kpi', this.widgetKey)
      this.$store.dispatch('fetchChartData', {
        key: this.widget.key,
        settings: this.widget.chartSettings,
        filters: this.$store.getters.isGenericGen ? this.$store.getters.selectedFiltersGen : this.$store.getters.selectedFilters,
        pagination: this.widget.pagination,
        withoutCount: true,
        numberFormatting: { formatNr: this.numberFormatting, decimal: this.useDecimal }
      })
    })
  },
  destroyed () {
    eventBus.$off('update-dashboard')
  },
  methods: {
  }
}
</script>

<style lang="scss">
  .kpi-card {
    height: 100%;
    .v-card__text {
      min-height: 107px;
    }
  }
</style>
